<script lang="ts">
  import type { AppController } from '../../app/controller.svelte';
  import { SLOTS } from '../../core/presets';
  import Toggle from '../components/Toggle.svelte';
  import Icon from '../components/Icon.svelte';

  let { ctl }: { ctl: AppController } = $props();
  const slots = $derived(ctl.presetsInBank(ctl.bank));
  let query = $state('');
  const library = $derived(
    ctl
      .allPresets()
      .filter((p) =>
        `${p.name} ${p.scene.tags.join(' ')}`.toLowerCase().includes(query.toLowerCase()),
      ),
  );
  let name = $state('');
  let fileInput: HTMLInputElement;
  let assigning = $state<string | null>(null);
  const keyLabel = (s: number) => (s === 9 ? '0' : String(s + 1));
  const favorites = $derived(ctl.favorites());
</script>

<section class="sec" data-tour="favorites">
  <div class="sec-head">
    <h2>Favorites</h2>
    <span class="chip">{favorites.length}</span>
    <button
      class="fav-btn"
      class:on={ctl.currentIsFavorite}
      data-testid="favorite-current"
      title={`Favorite what’s on screen${ctl.keyHint('favorite.add')}`}
      onclick={() => ctl.toggleFavorite()}
      ><Icon name={ctl.currentIsFavorite ? 'heart-filled' : 'heart'} />
      {ctl.currentIsFavorite ? 'Favorited' : 'Add current'}</button
    >
  </div>
  {#if favorites.length === 0}
    <p class="hint first">
      Press <Icon name="heart" /> or <kbd>L</kbd> on any look you like. Favorites are unlimited and
      don’t use bank slots. Step through them with <kbd>;</kbd> / <kbd>⇧;</kbd>, or let Auto show
      play them.
    </p>
  {:else}
    <div class="slots favorites" data-testid="favorites">
      {#each favorites as p (p.id)}
        <div class="slot" class:current={p.id === ctl.currentPresetId}>
          <button
            class="thumb"
            data-testid="favorite"
            title={p.name}
            style:background-image={p.thumbnail ? `url(${p.thumbnail})` : undefined}
            onclick={() => ctl.applyPreset(p)}
          ></button>
          <span class="slot-name">{p.name}</span>
          <button
            class="clear icon-btn"
            aria-label={`Remove ${p.name} from favorites`}
            title="Remove from favorites"
            onclick={() => ctl.library.setFavorite(p.id, false)}
            ><Icon name="heart-filled" /></button
          >
        </div>
      {/each}
    </div>
  {/if}
</section>

<section class="sec" data-tour="banks">
  <div class="sec-head">
    <div class="row">
      <button class="icon-btn" aria-label="Previous bank" onclick={() => ctl.setBank(ctl.bank - 1)}
        ><Icon name="chevron-left" /></button
      >
      <input
        class="bank-name"
        type="text"
        value={ctl.libraryVersion >= 0 ? ctl.library.bankName(ctl.bank) : ''}
        aria-label="Bank name"
        onchange={(e) => ctl.library.renameBank(ctl.bank, e.currentTarget.value)}
      />
      <button class="icon-btn" aria-label="Next bank" onclick={() => ctl.setBank(ctl.bank + 1)}
        ><Icon name="chevron-right" /></button
      >
    </div>
    <span class="chip">[ ] banks</span>
  </div>
  <div class="slots" data-testid="slots">
    {#each Array.from({ length: SLOTS }, (_, i) => i) as s (s)}
      {@const p = slots[s] ?? null}
      <div class="slot" class:current={p && p.id === ctl.currentPresetId} class:empty={!p}>
        <button
          class="thumb"
          data-testid={`slot-${s}`}
          title={p ? `${p.name} (key ${keyLabel(s)})` : `Empty: Shift+${keyLabel(s)} saves here`}
          style:background-image={p?.thumbnail ? `url(${p.thumbnail})` : undefined}
          onclick={(e) => (e.shiftKey || !p ? ctl.store(ctl.bank, s) : ctl.recall(ctl.bank, s))}
        >
          <span class="key">{keyLabel(s)}</span>
          {#if !p}<span class="plus"><Icon name="plus" /></span>{/if}
        </button>
        <span class="slot-name">{p?.name ?? ''}</span>
        {#if p}
          <button
            class="clear icon-btn"
            aria-label="Clear slot"
            title="Clear slot"
            onclick={() => ctl.library.assign(ctl.bank, s, null)}><Icon name="close" /></button
          >
        {/if}
      </div>
    {/each}
  </div>
  <p class="hint">
    Click to load (keys 1–0) · click an empty slot or Shift+click to save · Alt+key flashes while
    held.
  </p>
</section>

<section class="sec">
  <h2>Current scene</h2>
  <label class="field">
    <span>Name</span>
    <input
      type="text"
      value={ctl.scene.name}
      data-testid="scene-name"
      onchange={(e) => ctl.dispatch({ type: 'scene.rename', name: e.currentTarget.value })}
    />
  </label>
  <div class="row wrap">
    <button onclick={() => ctl.overwriteCurrent()} data-testid="save-preset">Save</button>
    <input
      type="text"
      placeholder="Save as…"
      bind:value={name}
      class="grow"
      aria-label="New preset name"
    />
    <button
      disabled={!name.trim()}
      onclick={() => {
        void ctl.saveAs(name.trim());
        name = '';
      }}>Save as</button
    >
  </div>
  <div class="row wrap actions">
    <button onclick={() => ctl.share()} data-testid="share">Copy share link</button>
    <button onclick={() => ctl.exportScene()}>Export</button>
    <button onclick={() => ctl.undo()} disabled={!ctl.canUndo} title="Undo (⌘/Ctrl+Z)">Undo</button>
    <button onclick={() => ctl.redo()} disabled={!ctl.canRedo} title="Redo (⇧⌘/Ctrl+Z)">Redo</button
    >
  </div>
</section>

<section class="sec">
  <div class="sec-head">
    <h2>Auto show</h2>
    {#if ctl.settings.autopilot && ctl.autoLast}<span class="chip accent">{ctl.autoLast}</span>{/if}
  </div>
  <Toggle
    label={`Run the show automatically${ctl.keyHint('autopilot.toggle')}`}
    checked={ctl.settings.autopilot}
    testid="autopilot"
    onchange={(v) => {
      if (v !== ctl.settings.autopilot) ctl.toggleAutoShow();
    }}
  />
  <p class="hint">
    Its options are in the <strong>Scene</strong> tab under <strong>Auto</strong>, including whole
    new looks from this bank, all presets or your favorites.
  </p>
</section>

<section class="sec">
  <div class="sec-head">
    <h2>Library</h2>
    <span class="chip">{library.length}</span>
  </div>
  <input
    type="search"
    placeholder="Search presets and tags"
    bind:value={query}
    class="search"
    aria-label="Search presets"
  />
  <div class="list lib">
    {#each library as p (p.id)}
      <div class="item" class:selected={p.id === ctl.currentPresetId}>
        <span
          class="mini-thumb"
          style:background-image={p.thumbnail ? `url(${p.thumbnail})` : undefined}
        ></span>
        <button
          class="name grow"
          onclick={() => ctl.applyPreset(p)}
          title={p.scene.tags.join(', ')}
        >
          {p.name}{#if p.builtIn}<span class="chip">built-in</span>{/if}
        </button>
        {#if assigning === p.id}
          <select
            aria-label="Assign to slot"
            onchange={(e) => {
              void ctl.library.assign(ctl.bank, Number(e.currentTarget.value), p.id);
              assigning = null;
            }}
          >
            <option value="">Slot…</option>
            {#each Array.from({ length: SLOTS }, (_, i) => i) as s (s)}<option value={s}
                >{keyLabel(s)}</option
              >{/each}
          </select>
        {:else}
          <button
            class="icon-btn"
            title="Put in a slot of this bank"
            onclick={() => (assigning = p.id)}><Icon name="assign" /></button
          >
        {/if}
        <button
          class="icon-btn"
          title="Export"
          aria-label="Export preset"
          onclick={() => ctl.exportPreset(p.id)}><Icon name="download" /></button
        >
        {#if !p.builtIn}
          <button
            class="icon-btn"
            title="Delete"
            aria-label="Delete preset"
            onclick={() => confirm(`Delete “${p.name}”?`) && ctl.library.remove(p.id)}
            ><Icon name="close" /></button
          >
        {/if}
      </div>
    {/each}
  </div>
</section>

<section class="sec">
  <h2>Import & export</h2>
  <div class="row wrap">
    <button onclick={() => fileInput.click()} data-testid="import">Import files…</button>
    <button onclick={() => ctl.exportBank()}>Export bank</button>
    <button onclick={() => ctl.exportLibrary()}>Export everything</button>
  </div>
  <input
    bind:this={fileInput}
    class="visually-hidden"
    aria-label="Choose files"
    type="file"
    multiple
    accept=".json,.fs,.frag,.isf,.glsl,.milk"
    data-testid="import-input"
    onchange={(e) => {
      const files = [...(e.currentTarget.files ?? [])];
      e.currentTarget.value = '';
      void ctl.importFiles(files);
    }}
  />
  <p class="hint">
    Presets, banks and libraries (.json), ISF shaders (.fs) and Butterchurn/MilkDrop presets (.json)
    all import here. You can also drop them on the page.
  </p>
</section>

<style>
  .bank-name {
    width: 150px;
    font-weight: 600;
  }
  .slots.favorites {
    max-height: 260px;
    overflow-y: auto;
  }
  .fav-btn {
    margin-left: auto;
    font-size: 12px;
    padding: 3px 10px;
  }
  .fav-btn.on {
    border-color: var(--accent-2);
    color: var(--accent-2);
  }
  .slots {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 6px;
  }
  .slot {
    position: relative;
    display: grid;
    gap: 2px;
  }
  .thumb {
    position: relative;
    aspect-ratio: 16 / 9;
    padding: 0;
    background-size: cover;
    background-position: center;
    background-color: rgba(255, 255, 255, 0.04);
    border-radius: 6px;
  }
  .slot.current .thumb {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }
  .slot.empty .thumb {
    border-style: dashed;
  }
  .key {
    position: absolute;
    top: 2px;
    left: 4px;
    font-family: var(--mono);
    font-size: 10px;
    color: #fff;
    text-shadow: 0 0 3px #000;
  }
  .plus {
    color: var(--faint);
  }
  .slot-name {
    font-size: 10.5px;
    color: var(--muted);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .clear {
    position: absolute;
    top: 0;
    right: 0;
    font-size: 10px;
    padding: 2px 4px;
    opacity: 0;
  }
  .slot:hover .clear {
    opacity: 1;
  }
  .actions {
    margin-top: 8px;
  }
  .search {
    width: 100%;
    margin-bottom: 8px;
  }
  .lib {
    max-height: 320px;
    overflow-y: auto;
  }
  .lib .name {
    border: none;
    background: none;
    text-align: left;
    padding: 2px;
    display: flex;
    gap: 6px;
    align-items: center;
    overflow: hidden;
    white-space: nowrap;
  }
  .mini-thumb {
    width: 36px;
    height: 20px;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.06) center / cover;
    flex: none;
  }
  .lib select {
    font-size: 12px;
    padding: 2px;
  }
</style>
